import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { getCustomerSession, isCustomerMarkedSignedIn } from './lib/customerSession';
import { isDriverSignedIn } from './lib/driverSession';
import OnboardingScreen from './components/OnboardingScreen';
import './App.css';
import CustomerLayout from './components/customer/CustomerLayout';
import CustomerAuthGate from './components/customer/CustomerAuthGate';
import ClientUpdateTaps from './components/customer/ClientUpdateTaps';
import SessionListener from './components/SessionListener';
import CustomerHomePage from './pages/CustomerHomePage';
import LoginPage from './pages/LoginPage';
import ChoosePasswordPage from './pages/ChoosePasswordPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import PublicDeleteAccountPage from './pages/PublicDeleteAccountPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import OrderHistoryPage from './pages/OrderHistoryPage';
import ProfilePage from './pages/ProfilePage';
import ProfileEditPage from './pages/ProfileEditPage';
import PackageDetailsPage from './pages/PackageDetailsPage';
import PriceEstimatePage from './pages/PriceEstimatePage';
import RegisterPage from './pages/RegisterPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import HelpSupportPage from './pages/HelpSupportPage';
import FAQsPage from './pages/FAQsPage';
import RequestDeliveryPage from './pages/RequestDeliveryPage';
import LiveTrackingPage from './pages/LiveTrackingPage';
import ChatPage from './pages/ChatPage';
import NotificationsPage from './pages/NotificationsPage';
import RateExperiencePage from './pages/RateExperiencePage';
import DriverLayout from './components/driver/DriverLayout';
import DriverAuthGate from './components/driver/DriverAuthGate';
import DriverRegisterPage from './pages/DriverRegisterPage';
import DriverApplicationPage from './pages/DriverApplicationPage';
import DriverChoosePasswordPage from './pages/DriverChoosePasswordPage';
import DriverLoginPage from './pages/DriverLoginPage';
import DriverHomePage from './pages/DriverHomePage';
import DriverOfferPage from './pages/DriverOfferPage';
import DriverOrdersPage from './pages/DriverOrdersPage';
import DriverEarningsPage from './pages/DriverEarningsPage';
import DriverWalletPage from './pages/DriverWalletPage';
import DriverProfilePage from './pages/DriverProfilePage';
import DriverNotificationsPage from './pages/DriverNotificationsPage';
import DriverSupportChatPage from './pages/DriverSupportChatPage';
import DriverCompanyFleetPage from './pages/DriverCompanyFleetPage';
import DriverActiveDeliveryPage from './pages/DriverActiveDeliveryPage';
import DriverPickupConfirmPage from './pages/DriverPickupConfirmPage';
import DriverNavigationPage from './pages/DriverNavigationPage';
import DriverCollectPaymentPage from './pages/DriverCollectPaymentPage';
import DriverRateCustomerPage from './pages/DriverRateCustomerPage';

const STORAGE_KEY = 'ingo_onboarding_complete';


function ScrollToTopOnRouteChange() {
  const location = useLocation();
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.history?.scrollRestoration) {
        window.history.scrollRestoration = 'manual';
      }
    } catch {
      // ignore
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    // Also reset common app shell scroll containers.
    document.querySelector('.driver-app__outlet')?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.querySelector('.customer-app__outlet')?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);
  return null;
}

function RootFlow() {
  // Show onboarding on first run; otherwise go to login (or home if already signed in).
  const [view, setView] = useState(() => {
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY) === '1') {
        return 'main';
      }
    } catch {
      // ignore
    }
    return 'onboarding';
  });

  const finishOnboarding = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore
    }
    setView('main');
  }, []);

  // The onboarding is the customer's. A signed-in driver never needs it, and on a
  // fresh install it sat in front of a driver who had just tapped an offer
  // notification on a killed app: the tap was parked, the driver shell that
  // collects it mounts only under /driver, and the screen showed "Fast
  // Deliveries / Skip" instead (emulator, 2026-09-06).
  if (view === 'onboarding' && !isDriverSignedIn()) {
    return <OnboardingScreen onComplete={finishOnboarding} />;
  }
  // Prefer the last active portal session so reopening the app skips customer login.
  if (isDriverSignedIn()) {
    return <Navigate to="/driver/home" replace />;
  }
  if (isCustomerMarkedSignedIn() && getCustomerSession()) {
    return <Navigate to="/home" replace />;
  }
  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTopOnRouteChange />
      <ClientUpdateTaps />
      {/* A dead token lands on sign-in; a carried-over password on choosing one. */}
      <SessionListener />
          <div className="App">
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset/:token" element={<ResetPasswordPage realm="customer" />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route
          path="/choose-password"
          element={
            <CustomerAuthGate>
              <ChoosePasswordPage />
            </CustomerAuthGate>
          }
        />
        {/* Deleting an account (InGo ADR 0038): in each profile, and the public page Google Play asks for. */}
        <Route
          path="/profile/delete-account"
          element={
            <CustomerAuthGate>
              <DeleteAccountPage realm="customer" />
            </CustomerAuthGate>
          }
        />
        <Route path="/driver/delete-account" element={<DeleteAccountPage realm="driver" />} />
        <Route path="/delete-account" element={<PublicDeleteAccountPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/help-support" element={<HelpSupportPage />} />
        <Route path="/faqs" element={<FAQsPage />} />
        <Route
          path="/request-delivery"
          element={
            <CustomerAuthGate>
              <RequestDeliveryPage />
            </CustomerAuthGate>
          }
        />
        <Route
          path="/package-details"
          element={
            <CustomerAuthGate>
              <PackageDetailsPage />
            </CustomerAuthGate>
          }
        />
        <Route
          path="/price-estimate"
          element={
            <CustomerAuthGate>
              <PriceEstimatePage />
            </CustomerAuthGate>
          }
        />
        <Route
          path="/live-tracking"
          element={
            <CustomerAuthGate>
              <LiveTrackingPage />
            </CustomerAuthGate>
          }
        />
        <Route
          path="/chat"
          element={
            <CustomerAuthGate>
              <ChatPage />
            </CustomerAuthGate>
          }
        />
        <Route
          path="/rate"
          element={
            <CustomerAuthGate>
              <RateExperiencePage />
            </CustomerAuthGate>
          }
        />
        <Route
          path="/rate/:orderId"
          element={
            <CustomerAuthGate>
              <RateExperiencePage />
            </CustomerAuthGate>
          }
        />
        <Route path="/" element={<RootFlow />} />
        <Route path="/driver/login" element={<DriverLoginPage />} />
        <Route path="/driver/register" element={<DriverRegisterPage />} />
        <Route path="/driver/application" element={<DriverApplicationPage />} />
        <Route path="/driver/choose-password" element={<DriverChoosePasswordPage />} />
        <Route path="/driver/reset/:token" element={<ResetPasswordPage realm="driver" />} />
        <Route
          path="/driver/active-delivery/:jobKey?"
          element={
            <DriverAuthGate>
              <DriverActiveDeliveryPage />
            </DriverAuthGate>
          }
        />
        <Route
          path="/driver/confirm-pickup/:jobKey?"
          element={
            <DriverAuthGate>
              <DriverPickupConfirmPage />
            </DriverAuthGate>
          }
        />
        <Route
          path="/driver/navigation/:jobKey?"
          element={
            <DriverAuthGate>
              <DriverNavigationPage />
            </DriverAuthGate>
          }
        />
        <Route
          path="/driver/collect-payment/:jobKey?"
          element={
            <DriverAuthGate>
              <DriverCollectPaymentPage />
            </DriverAuthGate>
          }
        />
        <Route
          path="/driver/rate-customer/:jobKey?"
          element={
            <DriverAuthGate>
              <DriverRateCustomerPage />
            </DriverAuthGate>
          }
        />
        <Route path="/driver" element={<DriverLayout />}>
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<DriverHomePage />} />
          <Route path="offer/:offerKey" element={<DriverOfferPage />} />
          <Route path="orders" element={<DriverOrdersPage />} />
          <Route path="earnings" element={<DriverEarningsPage />} />
          <Route path="wallet" element={<DriverWalletPage />} />
          <Route path="fleet" element={<DriverCompanyFleetPage />} />
          <Route path="profile" element={<DriverProfilePage />} />
          <Route path="notifications" element={<DriverNotificationsPage />} />
          <Route path="chat" element={<DriverSupportChatPage />} />
        </Route>
        {/* The client's screens answer only a signed-in client: a visitor signs in first. */}
        <Route
          element={
            <CustomerAuthGate>
              <CustomerLayout />
            </CustomerAuthGate>
          }
        >
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="home" element={<CustomerHomePage />} />
          <Route path="orders" element={<OrderHistoryPage />} />
          {/* The "Ingo Km" wallet is hidden for InGo clients (Rio, 2 Oct 2026): cash only today. */}
          <Route path="wallet/*" element={<Navigate to="/home" replace />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="profile/edit" element={<ProfileEditPage />} />
          <Route path="chat/support" element={<ChatPage support />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          </div>
    </BrowserRouter>
  );
}

export default App;
